import { usePermission } from 'hooks/usePermission';
import { AffiliationFieldKey } from 'pages/userManagement/authConfigs/affiliations/validateAffiliationFields';
import type { TPartialAttributesMatchingRow } from '@timeedit/types/lib/types';
import { Icon } from '@timeedit/ui-components';
import { Button, Input, Table } from '@timeedit/ui-components/antd';
// i18n
import intl from '../../../i18n/intl';
import AttributePairTableColumn from './AttributePairTableCell';
import OrgAndRoleCell from './OrgAndRoleCell';
import StandardOrganizationNodeCell from './StandardOrganizationNodeCell';
import UserObjectCell from './UserObjectCell';
import './index.scss';

const language: any = intl.messages;

type Props = {
  partialAttributes: TPartialAttributesMatchingRow[];
  onChange: (newState: TPartialAttributesMatchingRow[]) => void;
  /** Rows where FE validation caught specific invalid inputs → targeted cell borders. */
  failedFieldIndices?: Map<number, Set<AffiliationFieldKey>>;
  /** Rows where the API rejected the payload but no specific field is known → whole-row border. */
  failedRowIndices?: Set<number>;
};

const updatePartialAttributesFn = (
  currValue: TPartialAttributesMatchingRow[],
  newValue: any,
  rowIdx: number,
  colIdx: number
): TPartialAttributesMatchingRow[] => {
  const updatedPartialAttributes = [
    ...currValue.slice(0, rowIdx),
    [
      ...currValue[rowIdx].slice(0, colIdx),
      newValue,
      ...currValue[rowIdx].slice(colIdx + 1),
    ],
    ...currValue.slice(rowIdx + 1),
  ];
  return updatedPartialAttributes as TPartialAttributesMatchingRow[];
};

const createEmptyRow = (): TPartialAttributesMatchingRow => [
  [['', '']],
  [['', '']],
  '',
  [''],
  '',
];

const PartialAttributesMatchingTable = ({
  partialAttributes = [createEmptyRow()],
  onChange,
  failedFieldIndices,
  failedRowIndices,
}: Props) => {
  const canEdit = usePermission({
    permissions: ['TE_ADMIN::manageAuthConfigs'],
  });

  const onAddRow = () => {
    const newPartialAttributes = [...partialAttributes, createEmptyRow()];
    onChange(newPartialAttributes);
  };

  const onDeleteRow = (rowIdx: number) => {
    const updatedRow = [
      ...partialAttributes.slice(0, rowIdx),
      ...partialAttributes.slice(rowIdx + 1),
    ];
    onChange(updatedRow);
  };

  const onUpdateRow = (newValue: any, rowIdx: number, colIdx: number) => {
    const updatedRow = updatePartialAttributesFn(
      partialAttributes,
      newValue,
      rowIdx,
      colIdx
    );
    onChange(updatedRow);
  };

  return (
    <>
      <Table
        dataSource={partialAttributes}
        rowClassName={(_record, index) =>
          [
            'partial-attributes-table',
            // Only add the row-level error class for API-only failures where
            // we have no field-level information. Rows with fieldErrors get
            // targeted cell borders instead.
            failedRowIndices?.has(index) && !failedFieldIndices?.has(index)
              ? 'row-migration-error'
              : '',
          ]
            .filter(Boolean)
            .join(' ')
        }
        columns={[
          {
            title: 'Attributes & Values',
            dataIndex: undefined,
            render: (
              _: unknown,
              row: TPartialAttributesMatchingRow,
              rowIdx: number
            ) => (
              <AttributePairTableColumn
                attributePairs={row[0]}
                onChange={(value) => onUpdateRow(value, rowIdx, 0)}
                hasError={failedFieldIndices
                  ?.get(rowIdx)
                  ?.has('matchingCriteria')}
              />
            ),
          },
          {
            title: 'Nodes & roles',
            dataIndex: undefined,
            render: (
              _: unknown,
              row: TPartialAttributesMatchingRow,
              rowIdx: number
            ) => (
              <OrgAndRoleCell
                orgAndRolePairs={row[1]}
                onChange={(value) => onUpdateRow(value, rowIdx, 1)}
                hasError={failedFieldIndices
                  ?.get(rowIdx)
                  ?.has('organizationsAndRoles')}
              />
            ),
          },
          {
            title: 'Standard node',
            dataIndex: undefined,
            render: (
              _: unknown,
              row: TPartialAttributesMatchingRow,
              rowIdx: number
            ) => (
              <StandardOrganizationNodeCell
                standardOrganizationNodeId={row[2]}
                onChange={(value) => onUpdateRow(value, rowIdx, 2)}
              />
            ),
          },
          {
            title: 'User objects',
            dataIndex: undefined,
            render: (
              _: unknown,
              row: TPartialAttributesMatchingRow,
              rowIdx: number
            ) => (
              <UserObjectCell
                userObjects={row[3]}
                onChange={(value) => onUpdateRow(value, rowIdx, 3)}
              />
            ),
          },
          {
            title: 'Description',
            dataIndex: undefined,
            render: (
              _: unknown,
              row: TPartialAttributesMatchingRow,
              rowIdx: number
            ) => (
              <Input.TextArea
                style={{ marginTop: '4px' }}
                autoSize={{ minRows: 2, maxRows: 3 }}
                size="small"
                value={row[4] && row[4] !== '' ? row[4] : undefined}
                onChange={(e) => onUpdateRow(e.target.value, rowIdx, 4)}
                placeholder="Description"
              />
            ),
          },
          {
            title: '',
            dataIndex: undefined,
            key: 'action',
            width: 32,
            render: (_: unknown, __: unknown, rowIdx: number) => (
              <Button
                size="small"
                danger
                ghost
                onClick={() => onDeleteRow(rowIdx)}
                disabled={
                  !canEdit ||
                  !partialAttributes.length ||
                  partialAttributes.length <= 1
                }
                icon={<Icon id="DeleteOutlined" alt="Delete" />}
              />
            ),
          },
        ]}
        footer={() => (
          <Button
            type="default"
            size="small"
            icon={<Icon id="PlusOutlined" alt="Plus" />}
            onClick={() => onAddRow()}
          >
            {language['ui.action.add']}
          </Button>
        )}
        style={{ marginBottom: '16px', marginTop: '16px', width: '100%' }}
        rowKey="organization"
        pagination={false}
      />
    </>
  );
};

export default PartialAttributesMatchingTable;
